import React, { useState, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Accordion, AccordionSummary, AccordionDetails, Typography, TableBody, TableContainer } from '@mui/material';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../../uiComponents/MuiTable/StyledTable'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { renderTableByTableType,renderGnomadRaceRows,renderGnomadSexRows,renderOneKgRows } from './FormatDataHelper';
import './index.css';

const useStyles = makeStyles(theme => ({
  divRoot: {
    width: '100%',
    marginBottom: '.75rem',
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  },
  root: {
    background: '#006778',
    color: 'white',
    fontFamily: "Open Sans",
  },
  accordion: {
    border: '1px solid #999999'
  },
}));


const AnnotationData = ({ annotationData, fieldDescription, idPrefix = '' }) => {
  const classes = useStyles();
  const [sections, setSections] = useState([]); //section is each of an

  useEffect(() => {
    // console.log('--AnnotationData.componentDidMount()');
    //  form the anno table JSON for each anno section by checking the root JSON element for each anno source
    const sortedSections = [];
    annotationData.annotationSources.forEach((source)=>{
      const tables = [];
    Object.keys(source.annotationContent).map(key => {
      var element = source.annotationContent[key];
      
      // Handle clinvar and clinvar-preview FIRST before generic array processing
      if (key === "clinvar-preview") { 
        // filterout 'rcVs' node and format it properly for the RCVS table
          const { rcVs, ...filteredObject } = element;
          tables.push({ order: "desc", orderBy: "", tableName: "clinvar-preview", rows: renderAnnotationTable(filteredObject) });
          // rcVs in clinvar-preview already has the simple format, pass directly
          if (rcVs && Array.isArray(rcVs) && rcVs.length > 0) {
            tables.push({ order: "desc", orderBy: "", tableName: "RCVS", rows: rcVs });
          }
      } 
      else if (key === "clinvar") {
        // Handle clinvar similar to clinvar-preview: filter out complex nested objects
        if (Array.isArray(element)) {
          element.forEach((item, itemIndex) => {
            // Create a clean object by explicitly copying only allowed primitive properties
            const cleanItem = {};
            
            // Explicitly list and copy only simple primitive properties we want
            const allowedSimpleProps = ['version', 'accession', 'altAllele', 'refAllele', 
                                        'recordType', 'variantType', 'dateLastUpdated', 'isAlleleSpecific'];
            
            allowedSimpleProps.forEach(prop => {
              if (item[prop] !== undefined && item[prop] !== null && typeof item[prop] !== 'object') {
                cleanItem[prop] = item[prop];
              }
            });
            
            // Flatten classifications object into simple properties - extract only what we need
            if (item.classifications && item.classifications.germlineClassification) {
              const gc = item.classifications.germlineClassification;
              if (gc.classification !== undefined && typeof gc.classification !== 'object') {
                cleanItem.classification = gc.classification;
              }
              if (gc.reviewStatus !== undefined && typeof gc.reviewStatus !== 'object') {
                cleanItem.reviewStatus = gc.reviewStatus;
              }
              if (gc.dateLastEvaluated !== undefined && typeof gc.dateLastEvaluated !== 'object') {
                cleanItem.dateLastEvaluated = gc.dateLastEvaluated;
              }
              if (gc.mostRecentSubmission !== undefined && typeof gc.mostRecentSubmission !== 'object') {
                cleanItem.mostRecentSubmission = gc.mostRecentSubmission;
              }
              // Only include pubMedIds if it's an array of primitives
              if (gc.pubMedIds && Array.isArray(gc.pubMedIds) && gc.pubMedIds.every(id => typeof id !== 'object')) {
                cleanItem.pubMedIds = gc.pubMedIds;
              }
            }
            
            // Convert rcvs array to comma-separated accession numbers
            if (item.rcvs && Array.isArray(item.rcvs)) {
              const rcvAccessions = item.rcvs.map(rcv => {
                return rcv.accession && rcv.version ? `${rcv.accession}.${rcv.version}` : rcv.accession;
              }).filter(Boolean);
              if (rcvAccessions.length > 0) {
                cleanItem.rcvs = rcvAccessions.join(', ');
              }
            }
            
            // Convert clinicalAssertions array to comma-separated accession numbers
            if (item.clinicalAssertions && Array.isArray(item.clinicalAssertions)) {
              const caAccessions = item.clinicalAssertions.map(ca => ca.accession).filter(Boolean);
              if (caAccessions.length > 0) {
                cleanItem.clinicalAssertions = caAccessions.join(', ');
              }
            }
            
            // Create main table with flattened data
            var tableName = `clinvar${element.length > 1 ? ' - Item ' + (itemIndex + 1) : ''}`;
            tables.push({order: 'desc', orderBy: '', tableName, rows: renderAnnotationTable(cleanItem)});
          });
        } else {
          // Fallback for non-array clinvar data - use explicit whitelist approach
          const cleanElement = {};
          
          // Explicitly list allowed simple properties
          const allowedSimpleProps = ['version', 'accession', 'altAllele', 'refAllele', 
                                      'recordType', 'variantType', 'dateLastUpdated', 'isAlleleSpecific'];
          
          allowedSimpleProps.forEach(prop => {
            if (element[prop] !== undefined && element[prop] !== null && typeof element[prop] !== 'object') {
              cleanElement[prop] = element[prop];
            }
          });
          
          // Flatten classifications if present
          if (element.classifications && element.classifications.germlineClassification) {
            const gc = element.classifications.germlineClassification;
            if (gc.classification !== undefined && typeof gc.classification !== 'object') {
              cleanElement.classification = gc.classification;
            }
            if (gc.reviewStatus !== undefined && typeof gc.reviewStatus !== 'object') {
              cleanElement.reviewStatus = gc.reviewStatus;
            }
            if (gc.dateLastEvaluated !== undefined && typeof gc.dateLastEvaluated !== 'object') {
              cleanElement.dateLastEvaluated = gc.dateLastEvaluated;
            }
            if (gc.mostRecentSubmission !== undefined && typeof gc.mostRecentSubmission !== 'object') {
              cleanElement.mostRecentSubmission = gc.mostRecentSubmission;
            }
            if (gc.pubMedIds && Array.isArray(gc.pubMedIds) && gc.pubMedIds.every(id => typeof id !== 'object')) {
              cleanElement.pubMedIds = gc.pubMedIds;
            }
          }
          
          tables.push({order: 'desc', orderBy: '', tableName: key, rows: renderAnnotationTable(cleanElement)});
        }
      }
      //  Is the root element an array element?
      else if (JSON.stringify(element).startsWith("[")) {
          //  The root element is an array. Is there only one element in the array?
          if (Object.keys(element).length == 1) {
            // the root element is an array with only one element (probably a score or something). Is it a key-value pair?
            if ((element) == "[object Object]") {
              // it's a key-value pair. Is this gene data? If so, we need to go a level lower to get the fields.
              if ('gene' in element[0]){
                //TODO: need to revisit this gene/fields format, some are aray of genes, some array of feilds    
                if (typeof element[0].fields.length === "undefined" ){    
                  tables.push({order: 'desc', orderBy: '', "tableName": "Gene:" + element[0].gene, "rows": renderAnnotationTable(element[0].fields)});
                } else {
                  element[0].fields.forEach(field => {  
                    tables.push({order: 'desc', orderBy: '', "tableName": "Gene:" + element[0].gene, "rows": renderAnnotationTable(field)});
                  })
                }
              } else {
                //  the array has one element and it's just a value. Render a simple table
                tables.push({order: 'desc', orderBy: '', "tableName": key, "rows": renderAnnotationTable(element[0])});
              }
            } else {
              //  the array has one element and it's just a value. Render a simple table
              tables.push({order: 'desc', orderBy: '', "tableName": key, "rows": renderAnnotationTableWithSingleElement(key, element)});
            }
          } else {
            //  The root element is an array with multiple elements. Make a table for each element in the array
            element.forEach(item => {          
              var tableName = ('gene' in item) ? "Gene:" + item.gene : key;
              var rows = renderAnnotationTable(('gene' in item) ? item.fields : item);
              tables.push({order: 'desc', orderBy: '', tableName, rows});

            })//end foreach
          }
        
      } 
      else if (key.indexOf("gnomad") > -1){
        tables.push({ order: "desc", orderBy: "", tableName: "gnomadRace", rows: renderGnomadRaceRows(element) });
        tables.push({ order: "desc", orderBy: "", tableName: "gnomadSex", rows: renderGnomadSexRows(element) });
      }
      else if (key == "oneKg"){
        tables.push({ order: "desc", orderBy: "", tableName: "oneKg", rows: renderOneKgRows(element) });

      }
      else
      {
        //  the root element isn't an array. Does the root element have key values? 
        var rows =  ((element) == "[object Object]") 
                ? renderAnnotationTable(element) 
                : renderAnnotationTableWithSingleElement(key, element);  //  the root element is just a value
        tables.push({order: 'desc', orderBy: '', "tableName": key, "rows": rows});
      }
      
    });
    sortedSections.push({
      sourceFullName: source.fullSourceName,
      sectionHeader: source.fullSourceName + ", version " + source.version,
      sectionDescription: source.sourceDescription,
      tables: tables,
    });
  })//end of for each source
  
    // Sort sections so that those with data come first
    sortedSections.sort((a, b) => {
      const hasDataA = a.tables.some(table => table.rows.length > 0);
      const hasDataB = b.tables.some(table => table.rows.length > 0);
      return hasDataB - hasDataA; // Sort descending, sections with data first
    });

    setSections(sortedSections);
   
  }, [annotationData]);
  


  const renderAnnotationTable = (pairs) => {
    const rows = [];
    // Check if 'pairs' is an array (i.e., an array of objects)
    if (Array.isArray(pairs)) {
      pairs.forEach(pair => {
        // Iterate over each object in the array
        Object.keys(pair).forEach(key => {
          const value = pair[key];
          rows.push(renderRowElement(key,value))
        });
      });
    } else {
      // If it's a single object, process it as before
      for (const key of Object.keys(pairs)) {
        const value = pairs[key];
        rows.push(renderRowElement(key,value))
      }
    }
  
    return rows;
  };
  
  const renderRowElement=(key,value) =>{
    // Handle null or undefined
    if (value === null || value === undefined) {
      return ({ key, value: '', description: fieldDescription[key] });
    }
    
    // Handle arrays
    if (Array.isArray(value)) {
      // Check if array contains objects
      const hasObjects = value.some(item => typeof item === 'object' && item !== null);
      if (hasObjects) {
        // For arrays of objects, extract meaningful values or stringify
        const formatted = value.map(item => {
          if (typeof item === 'object' && item !== null) {
            // Try to find a meaningful property to display
            return item.accession || item.id || item.name || JSON.stringify(item);
          }
          return String(item);
        }).join(', ');
        return ({ key, value: formatted, description: fieldDescription[key] });
      }
      // For arrays of primitives, return as-is (will be handled by valueCell in FormatDataHelper)
      return ({ key, value, description: fieldDescription[key] });
    }
    
    // Handle objects (non-array)
    if (typeof value === 'object' && value !== null) {
      if (value.scores && Array.isArray(value.scores)) {
        return ({ key, value: value.scores.join(", "), description: fieldDescription[key] });
      }
      // For any other object, convert to JSON string
      return ({ key, value: JSON.stringify(value), description: fieldDescription[key] });
    } 
    
    // For primitive values (string, number, boolean)
    return ({ key, value, description: fieldDescription[key] });
  }

  const renderAnnotationTableWithSingleElement = (key, value) => {
    const rows = []; 
    rows.push({"key":key, "value":value, "description": fieldDescription[key] });
    return rows;
  };


  const handleRequestSort = (sectionIndex, tableIndex, property) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = newSections[sectionIndex];
      const table = section.tables[tableIndex];
      const isAsc = table.orderBy === property && table.order === 'asc';
      table.order = isAsc ? 'desc' : 'asc';
      table.orderBy = property;
      return newSections;
    });
  };


  const handleAnnotationData = () => {
    // Count how many times each section header appears so that duplicate sources
    // (e.g. two MITOMAP entries) can be given distinct accessible names. Landmark
    // regions rendered by MUI's Accordion must have unique accessible names.
    const headerCounts = {};
    sections.forEach(section => {
      headerCounts[section.sectionHeader] = (headerCounts[section.sectionHeader] || 0) + 1;
    });
    const headerOccurrences = {};

    return sections.length > 0 ? (
      // Render sections
      (sections.map((section, index) => {
        // Ensure section.tables is always an array
        const tables = section.tables || []; 
        // Build a unique id base so the Accordion's region landmark is distinguishable
        // across multiple AnnotationData instances rendered on the same page.
        const panelId = `${idPrefix ? idPrefix + '-' : ''}panel${index}`;
        // When the same header text repeats, append an occurrence number to the
        // landmark's accessible name so it stays unique (axe landmark-unique rule).
        headerOccurrences[section.sectionHeader] = (headerOccurrences[section.sectionHeader] || 0) + 1;
        const sectionLabel = headerCounts[section.sectionHeader] > 1
          ? `${section.sectionHeader} (${headerOccurrences[section.sectionHeader]})`
          : section.sectionHeader;
  
        return (
          <div key={`${section.sectionName}-${index}`} className={classes.divRoot}>
            <Accordion
              defaultExpanded={true}
              className={classes.accordion}
              elevation={2}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${panelId}-content`}
                aria-label={sectionLabel}
                id={`${panelId}-header`}
              >
                <Typography className={classes.heading}>{section.sectionHeader}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                  {section.sectionDescription && (
                    <Typography className="sectionDescription">{section.sectionDescription}</Typography>
                  )}
                  {tables.length > 0 ? (
                    tables.map((table, i) => (
                      <div
                        className="accordionInnerDiv"
                        key={`${section.sectionName}-${table.tableName || 'table'}-${i}`}
                      >
                        {table.rows.length > 0 ? (
                          <TableContainer component={StyledPaper}>
                            <StyledTable
                              size="small"
                              // Append name to each table in array, add table number if more than one table
                              aria-label={`${section.sourceFullName} table${tables.length > 1 ? ' ' + (i + 1) : ''}`} 
                            > 
                          
                                  {renderTableByTableType(table,index,i,handleRequestSort,section.sectionName)}
                            
                            </StyledTable>
                          </TableContainer>
                        ) : (
                          <Typography className="noDataMessage">No data available</Typography>
                        )}
                      </div>
                    ))
                  ) : (
                    <Typography className="noDataMessage">No data available</Typography>
                  )}
              </AccordionDetails>
            </Accordion>
          </div>
        );
      }))
    ) : (
      <div>No annotation data in this category was found for this variant.</div>
    );
  };
  
  return <div>{handleAnnotationData()}</div>;
};

export default AnnotationData;